// Symbols map keyed by ACT3 outcome code. Values are symbol-type keys rendered
// as inline SVGs in ScratchOff.tsx — close approximations of the Figma icons
// pending final asset export.
export const SCRATCH_SYMBOLS: Record<string, string> = {
  SUPER: 'ticket',
  LARGE: 'diamond',
  MEDIUM: 'star',
  SMALL: 'heart',
  NONE: 'club',
};

function shuffle<T>(arr: T[], rng: () => number): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Returns a 9-element array of symbol strings for a 3x3 scratch-off grid.
 *
 * Win outcome: the tier's symbol appears in exactly 3 randomly chosen panels;
 * the remaining 6 are filled with other symbols so no second symbol reaches
 * 3-of-a-kind.
 *
 * NONE: all 9 panels are filled so no symbol appears 3 or more times.
 *
 * Mirrors the wheelUtils single-export idiom.
 */
export function layoutPanelsForOutcome(
  outcome: string,
  symbols: Record<string, string>,
  rng: () => number = Math.random
): string[] {
  const allCodes = Object.keys(symbols);
  const winSymbol = symbols[outcome];

  if (outcome !== 'NONE') {
    // fail-closed: if outcome has no symbol entry, use a sentinel so the grid never
    // shows a losing layout (no 3-of-a-kind) while the backend pays a win.
    const sym = winSymbol ?? '★';
    const otherPool: string[] = [];
    for (const code of allCodes) {
      if (code !== outcome) {
        // At most 2 of each other symbol so no second 3-of-a-kind can form.
        otherPool.push(symbols[code], symbols[code]);
      }
    }
    const fillers = shuffle(otherPool, rng).slice(0, 6);
    return shuffle([sym, sym, sym, ...fillers], rng);
  }

  // NONE path: no symbol appears 3+ times.
  // 5 symbols × 2 = 10; take first 9 — max frequency of any symbol is 2.
  const pool: string[] = [];
  for (const code of allCodes) {
    pool.push(symbols[code], symbols[code]);
  }
  return shuffle(pool, rng).slice(0, 9);
}
